import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { combineLatest, Observable, Subject } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';

import {
  hasInvalidProduct,
  ProductSelection
} from '@features/opportunities/models/product-selection/product-selection.model';
import { Questionnaire } from '@features/opportunities/components/questionnaire-form/models/questionnaire';
import { formToPayload } from '@features/opportunities/components/questionnaire-form/models/questionnaire-form';
import { OpportunityWorkflowService } from '@features/opportunities/services/opportunity-workflow.service';
import { ProductSelectionService } from '@features/opportunities/services/product-selection.service';
import {
  CommandEvent,
  commandEvent
} from '@shared/components/load/command/command.component';
import { OpportunityEventService } from '@features/opportunities/services/opportunity-event.service';
import { OpportunitySummary } from '@features/opportunities/models/opportunity';
import { InvalidRequiredDocumentsPipe } from '@features/opportunities/pipes/workflow/invalid-required-documents.pipe';
import { HasSubmissionQuestionnairePipe } from '@features/opportunities/pipes/workflow/has-submission-questionnaire.pipe';

import { SubmissionStatusService } from '@features/opportunities/services/submission-status.service';
import { SubmissionStatusModalComponent } from '../submission-status-modal/submission-status-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TextAttributes } from '@shared/enums/customisation.enum';
import { MonitoringService } from '@core/services/monitoring.service';
import { MonitoringEvents } from '@core/services/monitoring-events.constants';

@Component({
  selector: 'app-opportunity-workflow-submission',
  templateUrl: './opportunity-workflow-submission.component.html',
  styleUrls: ['./opportunity-workflow-submission.component.scss']
})
export class OpportunityWorkflowSubmissionComponent implements OnInit {
  opportunityId!: string;
  private questionnaire$!: Observable<Questionnaire>;
  private productSelection$!: Observable<ProductSelection | null>;
  load$!: Observable<{
    questionnaire: Questionnaire | undefined;
    productSelection: ProductSelection | null;
    opportunitySummary: OpportunitySummary;
  }>;
  private questionnaire!: Questionnaire;
  opportunitySummary!: OpportunitySummary;

  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  questionnaireForm = new UntypedFormGroup({});

  submitForm = new UntypedFormGroup({
    productSelection: new UntypedFormControl(null, [
      Validators.required,
      this.invalidProductValidator()
    ]),
    summary: new UntypedFormControl(),
    questionnaire: this.questionnaireForm
  });

  consentToLfiContactOptions = ['Yes', 'No'];

  private invalidProductValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (formControl.value == undefined) {
        return {
          noRecommendedProduct:
            'A product recommendation is required prior to submitting the opportunity'
        };
      } else if (hasInvalidProduct(formControl.value)) {
        return { invalidProduct: 'Recommended product is no longer valid' };
      } else {
        return null;
      }
    };
  }

  private submissionChanges$: Subject<any> = new Subject<any>();
  public submit$: Observable<CommandEvent<string>> = commandEvent(
    this.submissionChanges$,
    (payload) =>
      this.opportunityWorkflowService
        .updateSubmission(this.opportunityId, payload)
        .pipe(
          switchMap((_) =>
            this.opportunityWorkflowService.submit(this.opportunityId)
          )
        )
  );

  constructor(
    private opportunityWorkflowService: OpportunityWorkflowService,
    private productSelectionService: ProductSelectionService,
    private opportunityEventService: OpportunityEventService,
    private submissionStatusService: SubmissionStatusService,
    private modalService: BsModalService,
    private monitoringService: MonitoringService
  ) {}

  TextAttributes = TextAttributes;

  ngOnInit(): void {
    this.questionnaire$ = this.opportunityWorkflowService
      .getSubmissionQuestions(this.opportunityId)
      .pipe(tap((x) => (this.questionnaire = x)));

    this.productSelection$ = this.productSelectionService
      .getRecommendedProduct(this.opportunityId)
      .pipe(
        tap((po) => {
          this.submitForm.controls.productSelection.patchValue(po);
        })
      );

    this.load$ = combineLatest([
      this.productSelection$,
      this.questionnaire$,
      this.opportunityEventService.selectedOpportunitySummary$
    ]).pipe(
      tap(([, , opportunitySummary]) => {
        this.opportunitySummary = opportunitySummary;
      }),
      map(([productSelection, questionnaire, opportunitySummary]) => ({
        questionnaire,
        productSelection,
        opportunitySummary,
      })),
    );

    this.submitForm.controls.summary.markAsDirty();
  }

  get showSave() {
    const invalidDocuments = new InvalidRequiredDocumentsPipe();
    return this.opportunitySummary
      ? invalidDocuments.transform(this.opportunitySummary).length === 0
      : false;
  }

  submit(): void {
    if (this.submitForm.invalid) {
      return;
    }

    const val = this.submitForm.value;
    const summary = val.summary?.trim();
    const hasQuestionnairePipe = new HasSubmissionQuestionnairePipe();

    // Track submission started
    this.monitoringService.logEvent(MonitoringEvents.SUBMISSION_STARTED, {
      opportunityId: this.opportunityId,
      opportunityType: this.opportunitySummary.summary.opportunityType
    });
    
    const showModal = this.submissionStatusService.shouldShowSubmissionModal();

    if (showModal) {
      this.modalService.show(SubmissionStatusModalComponent, {
        class: 'modal-dialog-centered',
        initialState: {
          opportunityId: this.opportunityId,
          opportunityType: this.opportunitySummary.summary.opportunityType
        }
      });
    }

    this.submissionChanges$.next({
      questionnaire: hasQuestionnairePipe.transform(val.productSelection)
        ? formToPayload(this.questionnaire, this.questionnaireForm)
        : undefined,
      summary: summary?.length > 0 ? summary : undefined,
      productId: val.productSelection.id
    });
  }
}
